/**
 * ApiService — centralizovani servis za sve HTTP zahteve.
 *
 * Odgovornosti:
 * - Automatsko dodavanje JWT tokena u svaki zahtev
 * - Token renewal na 401 / auth 403 (bez strukturiranog `code` u body-u)
 * - X-Role header rezolucija (policy registry + globalni fallback)
 * - Keširanje loginData po tokenu — jedan in-flight /login za konkurentne pozive
 * - Javni endpoint-i: token se šalje ako postoji, inače anonimni poziv
 */

import AuthService from '../auth/AuthService';
import axiosInstance from './axiosConfig';
import authDebug from '../../shared/utils/authDebug';
import {
  normalizePlatformRoles,
  resolveApiXRoleHeader,
  resolveRequestedXRole,
} from '../../shared/auth/obligationAuth';
import { resolveRoleForEndpoint } from './endpointRolePolicy';

/** Jedan in-flight zahtev za učitavanje loginData — svi ApiService pozivi dele isti rezultat. */
let ensureLoginDataPromise = null;

/** Keš login podataka po tokenu — posle prvog uspešnog /login sledeći pozivi koriste keš. */
let cachedLoginData = null;

/** Guard koji sprečava višestruke `sessionExpired` event-e i duplirane toast-ove. Reset posle 10s. */
let sessionExpiredDispatched = false;

/**
 * Emituje `sessionExpired` CustomEvent najviše jednom u 10s prozoru.
 * @param {object} detail - Detalji greške za AuthContext listener.
 * @returns {void}
 */
function dispatchSessionExpiredOnce(detail) {
  if (sessionExpiredDispatched) return;
  sessionExpiredDispatched = true;
  setTimeout(() => { sessionExpiredDispatched = false; }, 10000);
  window.dispatchEvent(new CustomEvent('sessionExpired', { detail }));
}

/**
 * Proverava da li HTTP greška zahteva token renewal.
 * 401 → uvek renewal; 403 sa strukturiranim `code` → biznis greška (bez renewal); 403 bez `code` → auth greška.
 *
 * @param {import('axios').AxiosError} error - Axios greška.
 * @returns {boolean} `true` ako treba pokušati renewal.
 */
function shouldAttemptRenewal(error) {
  const status = error?.response?.status;
  if (status === 401) return true;
  if (status === 403) {
    const code = error?.response?.data?.code;
    return !code;
  }
  return false;
}

/**
 * Greška istekle sesije. Pozivači je prepoznaju da preskoče sopstveni toast —
 * AuthContext već prikazuje poruku putem `sessionExpired` eventa.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super('Sesija je istekla. Molimo prijavite se ponovo.');
    this.name = 'SessionExpiredError';
  }
}

/**
 * Proverava da li vrednost liči na AbortSignal.
 * @param {*} value - Vrednost za proveru.
 * @returns {boolean}
 */
function isAbortSignalLike(value) {
  return (
    value != null
    && typeof value === 'object'
    && typeof value.aborted === 'boolean'
    && typeof value.addEventListener === 'function'
  );
}

/**
 * Briše keš login podataka (npr. pri odjavi ili neuspešnom renewal-u).
 * @returns {void}
 */
export function clearLoginDataCache() {
  cachedLoginData = null;
  ensureLoginDataPromise = null;
}

/**
 * Postavlja keš login podataka (npr. kada App loadUser dobije odgovor od checkLogin).
 * Sprečava drugi /login poziv kada prvi API poziv pozove `_ensureLoginData`.
 *
 * @param {string} token - access_token trenutnog korisnika.
 * @param {{ profile: object, roles: string[], organizations: object }} data - Login podaci sa BE.
 * @returns {void}
 */
export function setCachedLoginData(token, data) {
  if (token && data) {
    cachedLoginData = { token, data };
  }
}

/**
 * Vraća keširane login podatke za dati token (bez HTTP poziva).
 *
 * @param {string} token - access_token trenutnog korisnika.
 * @returns {{ profile: object, roles: string[], organizations: object }|null} Keširani podaci ili `null`.
 */
export function getCachedLoginData(token) {
  if (!token || !cachedLoginData || cachedLoginData.token !== token) return null;
  return cachedLoginData.data;
}

/** Javni endpoint-i koji ne zahtevaju autentifikaciju, ali šalju token ako korisnik postoji. */
const PUBLIC_ENDPOINTS = [
  '/notice/search',
  '/notice/search-one',
  '/seo/search-one',
  '/notice/dictionary/search',
  '/action-plan/search',
  '/action-plan/search-v2',
  '/document/search',
  '/document/download',
  '/document/comment/search',
  '/obligation/search',
  '/obligation/proposal/search',
  '/obligation/proposal/search-one',
  '/obligation/proposal/archive/search',
  '/obligation/activity/search',
  '/obligation/activity/proposal/search',
  /** Početna CTA „U sprovođenju”: ukupni status/opis bez prijave. */
  '/obligation/implementation/search',
];

/**
 * Centralizovani API servis — singleton instanca se eksportuje kao default.
 */
class ApiService {

  /**
   * Izračunava X-Role string iz JWT uloga korisnika (priority lista).
   *
   * @private
   * @param {object|null} user - User objekat sa `loginData.roles`.
   * @returns {string|null} Naziv uloge ili `null`.
   */
  _calculateIdRole(user) {
    if (!user) return null;
    const roles = normalizePlatformRoles(user);
    if (!roles.length) return null;
    return resolveApiXRoleHeader(user);
  }

  /**
   * Učitava `loginData` na user ako nedostaje. Jedan poziv /login za sve konkurentne zahteve.
   *
   * @private
   * @param {object|null} user - OIDC User objekat.
   * @param {boolean} isLoginEndpoint - Da li je trenutni zahtev login/auth endpoint.
   * @returns {Promise<void>}
   */
  async _ensureLoginData(user, isLoginEndpoint) {
    if (isLoginEndpoint || !user || !user.access_token) return;
    if (user.loginData) return;
    const token = user.access_token;
    if (cachedLoginData && cachedLoginData.token === token) {
      user.loginData = cachedLoginData.data;
      return;
    }
    if (ensureLoginDataPromise) {
      try {
        const data = await ensureLoginDataPromise;
        if (data) {
          user.loginData = data;
          cachedLoginData = { token, data };
        }
      } catch (e) { /* silent */ }
      return;
    }
    const AccountService = (await import('../../shared/services/AccountService')).default;
    ensureLoginDataPromise = AccountService.checkLogin()
      .then((res) => res ? { profile: res.profile || {}, roles: res.roles || [], organizations: res.organizations || { requested: [], verified: [] } } : null)
      .finally(() => { ensureLoginDataPromise = null; });
    try {
      const data = await ensureLoginDataPromise;
      if (data) {
        user.loginData = data;
        cachedLoginData = { token, data };
      }
    } catch (e) { /* silent */ }
  }

  /**
   * Glavni metod za API pozive. Automatski upravlja autentikacijom, token renewal-om i X-Role header-om.
   *
   * @param {import('axios').AxiosRequestConfig} req - Axios request config.
   * @param {string} req.method - HTTP metod (GET, POST, PUT, DELETE, itd.).
   * @param {string} req.url - Endpoint URL (npr. `/ogpbe/proposals`).
   * @param {object} [req.data] - Request body (POST/PUT/PATCH).
   * @param {object} [req.params] - URL query parametri (GET).
   * @param {object} [req.headers] - Dodatni headers (npr. `X-Role` override).
   * @param {AbortSignal} [req.signal] - Signal za otkazivanje zahteva.
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  callApi(req) {
    const isAuthRequest = req.url && (req.url.includes('/login') || req.url.includes('/auth'));
    const isPublicEndpoint = req.url && PUBLIC_ENDPOINTS.some(endpoint => req.url.includes(endpoint));

    return AuthService.getUser().then(async (user) => {
      if (isAuthRequest) {
        authDebug.token('Provera tokena', user?.access_token);
      }

      if (isPublicEndpoint) {
        if (user && user.access_token) {
          const isLoginEndpoint = req.url && (req.url.includes('/login') || req.url.includes('/auth'));
          await this._ensureLoginData(user, isLoginEndpoint);
          const idRole = this._calculateIdRole(user);
          return this._callApi(user.access_token, idRole, req, user);
        } else {
          return this._callApiPublic(req, user);
        }
      }

      const isLoginEndpoint = req.url && (req.url.includes('/login') || req.url.includes('/auth'));
      await this._ensureLoginData(user, isLoginEndpoint);
      const idRole = this._calculateIdRole(user);

      if (user && user.access_token) {
        return this._callApi(user.access_token, idRole, req, user).catch((error) => {
          if (!shouldAttemptRenewal(error)) {
            throw error;
          }

          if (isAuthRequest) {
            authDebug.warning(`Auth greška (${error.response?.status}), pokušavam renewal`);
          }

          return AuthService.renewToken().then(async (renewedUser) => {
            if (isAuthRequest) {
              authDebug.success('Token obnovljen, ponavljam zahtev');
            }
            await this._ensureLoginData(renewedUser, isLoginEndpoint);
            const renewedIdRole = this._calculateIdRole(renewedUser);
            return this._callApi(renewedUser.access_token, renewedIdRole, req, renewedUser);
          }).catch(async (renewError) => {
            authDebug.error('Token renewal neuspešan, sesija je istekla', renewError);

            const errorMessage = renewError.message?.toLowerCase() || '';
            const errorCode = String(renewError.code || renewError.error || '').toLowerCase();
            const isNetworkError =
              errorMessage.includes('network') || errorMessage.includes('timeout') ||
              errorMessage.includes('econnaborted') || errorMessage.includes('failed to fetch') ||
              errorCode === 'econnaborted' || errorCode === 'timeout' ||
              renewError.name === 'NetworkError';
            const isAuthErrorDetail =
              errorCode === 'login_required' || errorCode === 'interaction_required' ||
              errorMessage.includes('session') || errorMessage.includes('expired') ||
              errorMessage.includes('invalid') || errorMessage.includes('unauthorized');

            try { clearLoginDataCache(); await AuthService.removeUser(); } catch {}
            try {
              const keys = [];
              for (let i = 0; i < sessionStorage.length; i++) {
                const k = sessionStorage.key(i);
                if (k && (k.includes('oidc') || k.includes('Oidc'))) keys.push(k);
              }
              keys.forEach((k) => sessionStorage.removeItem(k));
            } catch {}
            try {
              document.cookie.split(';').forEach((c) => {
                document.cookie = c.replace(/^ +/, '').replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
              });
            } catch {}

            // Tokom interactive login-a (/login, /auth endpoint), renewal failure je
            // "prijava nije uspela" — ne "sesija istekla". Ne emitujemo sessionExpired event
            // jer bi AuthContext pokazao pogrešan toast i setovao user=null usred OIDC callback-a.
            // Session cleanup (clearLoginDataCache, removeUser, storage) se svakako desio gore.
            if (!isAuthRequest) {
              dispatchSessionExpiredOnce({
                reason: 'Token renewal failed',
                isNetworkError,
                isAuthError: isAuthErrorDetail,
                errorMessage: errorMessage || 'Unknown error',
              });
            }

            throw new SessionExpiredError();
          });
        });
      } else if (user) {
        if (isAuthRequest) {
          authDebug.warning('Korisnik postoji ali nema token, obnavljam');
        }
        return AuthService.renewToken().then(async (renewedUser) => {
          if (isAuthRequest) {
            authDebug.success('Token obnovljen');
          }
          await this._ensureLoginData(renewedUser, isLoginEndpoint);
          const renewedIdRole = this._calculateIdRole(renewedUser);
          return this._callApi(renewedUser.access_token, renewedIdRole, req, renewedUser);
        }).catch(async (renewError) => {
          authDebug.error('Token renewal neuspešan', renewError);
          try { clearLoginDataCache(); await AuthService.removeUser(); } catch {}
          try {
            const keys = [];
            for (let i = 0; i < sessionStorage.length; i++) {
              const k = sessionStorage.key(i);
              if (k && (k.includes('oidc') || k.includes('Oidc'))) keys.push(k);
            }
            keys.forEach((k) => sessionStorage.removeItem(k));
          } catch {}
          try {
            document.cookie.split(';').forEach((c) => {
              document.cookie = c.replace(/^ +/, '').replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
            });
          } catch {}
          if (!isAuthRequest) {
            dispatchSessionExpiredOnce({ reason: 'Token renewal failed' });
          }
          throw new SessionExpiredError();
        });
      } else {
        if (isAuthRequest) {
          authDebug.error('Korisnik nije prijavljen');
        }
        throw new Error('Korisnik nije prijavljen');
      }
    });
  }

  /**
   * Interni metod za HTTP zahtev bez autentifikacije (javni endpoint-i).
   *
   * @private
   * @param {import('axios').AxiosRequestConfig} req - Axios request config.
   * @param {object|null} [user] - User objekat za X-Role rezoluciju (opciono).
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  _callApiPublic(req, user = null) {
    const requestedRole = req.headers?.['X-Role'];

    let xRole;
    if (requestedRole) {
      xRole = user
        ? resolveRequestedXRole(user, requestedRole, { allowFunctionalModerator: requestedRole === 'MODERATOR' })
        : requestedRole;
    } else if (user) {
      const policyRole = resolveRoleForEndpoint(req.url, user);
      xRole = policyRole ?? resolveApiXRoleHeader(user);
    } else {
      xRole = 'ADMIN';
    }

    const { 'X-Role': _, ...reqHeadersWithoutXRole } = req.headers || {};

    const isFormData =
      typeof FormData !== 'undefined' && req.data instanceof FormData;
    const method = String(req.method || 'GET').toUpperCase();
    const sendsJsonBody =
      !isFormData
      && req.data !== undefined
      && req.data !== null
      && ['POST', 'PUT', 'PATCH'].includes(method);
    const jsonContentType = sendsJsonBody ? { 'Content-Type': 'application/json' } : {};

    return axiosInstance({
      ...req,
      headers: { ...reqHeadersWithoutXRole, 'X-Role': xRole, ...jsonContentType },
      signal: req.signal,
    });
  }

  /**
   * Interni metod koji šalje HTTP zahtev sa JWT tokenom i X-Role header-om.
   *
   * @private
   * @param {string} token - JWT access token.
   * @param {string|null} idRole - Uloga iz login response-a (fallback).
   * @param {import('axios').AxiosRequestConfig} req - Axios request config.
   * @param {object|null} [user] - User objekat za X-Role validaciju.
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  _callApi(token, idRole, req, user = null) {
    const bearerToken = token && !token.startsWith('Bearer ')
      ? `Bearer ${token}`
      : token;

    const requestedRole = req.headers?.['X-Role'];
    const xRole = requestedRole
      ? resolveRequestedXRole(user, requestedRole, { allowFunctionalModerator: requestedRole === 'MODERATOR' })
      : (resolveRoleForEndpoint(req.url, user) ?? resolveApiXRoleHeader(user) ?? idRole ?? 'CITIZEN');

    const authHeaders = {
      Authorization: bearerToken,
      'X-Role': xRole,
    };

    const { 'X-Role': _, ...reqHeadersWithoutXRole } = req.headers || {};

    const isFormData =
      typeof FormData !== 'undefined' && req.data instanceof FormData;
    const method = String(req.method || 'GET').toUpperCase();
    const sendsJsonBody =
      !isFormData
      && req.data !== undefined
      && req.data !== null
      && ['POST', 'PUT', 'PATCH'].includes(method);
    const jsonContentType = sendsJsonBody ? { 'Content-Type': 'application/json' } : {};

    return axiosInstance({
      ...req,
      headers: { ...reqHeadersWithoutXRole, ...authHeaders, ...jsonContentType },
      signal: req.signal,
    });
  }

  /**
   * Helper za GET zahteve.
   *
   * @param {string} url - Endpoint URL.
   * @param {object} [params] - URL query parametri.
   * @param {AbortSignal|null} [signal] - Signal za otkazivanje zahteva.
   * @param {object} [headers] - Dodatni headers (npr. `{ 'X-Role': 'CITIZEN' }`).
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  get(url, params = {}, signal = null, headers = {}) {
    return this.callApi({
      method: 'GET',
      url,
      params,
      signal,
      headers,
    });
  }

  /**
   * Helper za POST zahteve. Podržava `post(url, data, abortSignal)` legacy poziv.
   *
   * @param {string} url - Endpoint URL.
   * @param {object} data - Request body.
   * @param {object|AbortSignal} [headers] - Dodatni headers ili AbortSignal (3. argument).
   * @param {AbortSignal|null} [signal] - Signal za otkazivanje zahteva.
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  post(url, data, headers = {}, signal = null) {
    let resolvedHeaders = headers;
    let resolvedSignal = signal;

    if (resolvedSignal == null && isAbortSignalLike(resolvedHeaders)) {
      resolvedSignal = resolvedHeaders;
      resolvedHeaders = {};
    } else if (
      resolvedSignal == null
      && resolvedHeaders
      && typeof resolvedHeaders === 'object'
      && isAbortSignalLike(resolvedHeaders.signal)
      && !resolvedHeaders.Authorization
      && !resolvedHeaders['X-Role']
      && !resolvedHeaders['Content-Type']
    ) {
      resolvedSignal = resolvedHeaders.signal;
      resolvedHeaders = resolvedHeaders.headers || {};
    }

    return this.callApi({
      method: 'POST',
      url,
      data,
      headers: resolvedHeaders || {},
      signal: resolvedSignal,
    });
  }

  /**
   * Helper za PUT zahteve.
   *
   * @param {string} url - Endpoint URL.
   * @param {object} data - Request body.
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  put(url, data) {
    return this.callApi({
      method: 'PUT',
      url,
      data,
    });
  }

  /**
   * Helper za DELETE zahteve.
   *
   * @param {string} url - Endpoint URL.
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  delete(url) {
    return this.callApi({
      method: 'DELETE',
      url,
    });
  }
}

const apiServiceInstance = new ApiService();
export default apiServiceInstance;
